import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { GoogleGenerativeAI } from '@google/generative-ai';
import './llama style/rankings.css';

// ── Gemini setup ───────────────────────────────────────────────────────────────
const genAI      = new GoogleGenerativeAI("AIzaSyD_cIDIcJR3OolY3piQxozRG4Zccr2thPk");
const gapModel   = genAI.getGenerativeModel({
  model: 'gemini-2.5-flash-lite',
  generationConfig: { temperature: 0.7, maxOutputTokens: 1600 },
});

// ── OpenAI helper ─────────────────────────────────────────────────────────────
const OPENAI_KEY = "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";

async function askOpenAI(prompt) {
  if (!OPENAI_KEY) throw new Error('Missing REACT_APP_OPENAI_API_KEY.');
  const resp = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: { Authorization: `Bearer ${OPENAI_KEY}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'gpt-3.5-turbo',
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.7,
      max_tokens: 1600,
    }),
  });
  if (!resp.ok) throw new Error(`OpenAI ${resp.status}`);
  const data = await resp.json();
  const content = data?.choices?.[0]?.message?.content;
  if (!content) throw new Error('OpenAI returned no content.');
  return content;
}

// ── Country code → name map ────────────────────────────────────────────────────
const COUNTRY_NAMES = {
  IN:'India', CN:'China', CA:'Canada', MX:'Mexico', PK:'Pakistan',
  KR:'South Korea', AE:'UAE', BR:'Brazil', NG:'Nigeria', GB:'United Kingdom', XX:'International',
};

function buildGapPrompt(uni, profile) {
  const country = profile.country ? COUNTRY_NAMES[profile.country] || profile.country : null;
  return (
`You are an expert US college admissions counselor specializing in international students.
Provide a specific, honest, holistic gap analysis for this student applying to this school.
Base your assessment on ALL profile factors provided — not just test scores.

SCHOOL: ${uni.name} (${uni.city}, ${uni.state})
- Avg SAT: ${uni.avgSAT ?? 'Not reported'}
- Acceptance Rate: ${uni.acceptanceRate != null ? (uni.acceptanceRate * 100).toFixed(1) + '%' : 'Not reported'}
- Type: ${SCHOOL_TYPE_LABELS[uni.type] ?? 'Unknown'}
- Enrollment: ${uni.size?.toLocaleString() ?? 'Unknown'}
- Intl. Tuition: ${uni.tuition ? '$' + uni.tuition.toLocaleString() : 'Not reported'}

STUDENT PROFILE:
${country ? `- Country: ${country}` : ''}
- Test Scores (SAT/ACT): ${profile.testScores ?? 'Not provided'}
- Academic Record: ${profile.academicRecord ?? 'Not provided'}
- English Proficiency (IELTS/TOEFL/Duolingo): ${profile.englishProficiency ?? 'Not provided'}
- Extracurricular Activities: ${profile.extracurriculars ?? 'Not provided'}
- Volunteering & Community Service: ${profile.volunteering ?? 'Not provided'}
- Intended Major: ${profile.intendedMajor ?? 'Not provided'}

Respond in EXACTLY this format (no markdown, no asterisks, no extra headers):

OVERALL FIT: [Likely / Match / Reach / Significant Reach] — one honest sentence weighing all factors.

QUANTITATIVE:
• Academic Record: [assess their grades in context of their country's grading system; how competitive is this for typical admits at this school]
• Test Scores: [compare SAT/ACT to school median; if not provided explain what that means for the application]
• English Proficiency: [assess their IELTS/TOEFL/Duolingo score against typical school requirements; if not provided note the gap]
• Selectivity: [what the acceptance rate realistically means for an international applicant with this overall profile]
• Cost: [tuition viability; remind that international students rarely receive need-based aid at most US schools]

STRENGTHS:
• [strength 1 — specific to this student's profile and this school's priorities]
• [strength 2 — draw from academics, ECs, volunteering, or major alignment]
• [strength 3 if applicable]

GAPS:
• [gap 1 — specific weakness, with a concrete actionable step to address it]
• [gap 2 — same level of specificity]
• [gap 3 if applicable]

BOTTOM LINE: [2–3 direct, honest sentences covering academic fit, extracurricular strength, and realistic admission chances. Do not sugarcoat.]`
  );
}

const API_KEY = "PCMdoitL3UGD6Y3arascSplbLEGuHUvz0M6hbUz9";

// ── Profile helpers ────────────────────────────────────────────────────────────
function loadStudentProfile() {
  try {
    const uid = getAuth().currentUser?.uid;
    const key = uid ? `studentProfile_${uid}` : 'studentProfile_guest';
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function parseSATScore(text) {
  if (!text) return null;
  const m = String(text).match(/\b(1[0-5]\d{2}|[4-9]\d{2})\b/);
  if (!m) return null;
  const n = parseInt(m[1]);
  return n >= 400 && n <= 1600 ? n : null;
}

// ── Shortlist helpers ──────────────────────────────────────────────────────────
function shortlistKey() {
  const uid = getAuth().currentUser?.uid;
  return uid ? `savedColleges_${uid}` : 'savedColleges_guest';
}
function loadShortlist() {
  try { const d = localStorage.getItem(shortlistKey()); return d ? JSON.parse(d) : []; }
  catch { return []; }
}
function persistShortlist(list) {
  try { localStorage.setItem(shortlistKey(), JSON.stringify(list)); } catch {}
}
const PER_PAGE = 20;

const STATE_OPTIONS = [
  'All','AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID',
  'IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO',
  'MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA',
  'RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

const SCHOOL_TYPE_LABELS = { 1: 'Public', 2: 'Private Nonprofit', 3: 'Private For-Profit' };

const ACCEPTANCE_BUCKETS = [
  { label: 'All',                    min: 0,  max: 100 },
  { label: 'Highly Selective <10%',  min: 0,  max: 10  },
  { label: 'Very Selective 10–25%',  min: 10, max: 25  },
  { label: 'Selective 25–50%',       min: 25, max: 50  },
  { label: 'Accessible 50–75%',      min: 50, max: 75  },
  { label: 'Open Admission 75%+',    min: 75, max: 101 },
];

const TUITION_BUCKETS = [
  { label: 'All',         max: Infinity },
  { label: 'Under $15k',  max: 15000    },
  { label: 'Under $25k',  max: 25000    },
  { label: 'Under $40k',  max: 40000    },
  { label: 'Under $60k',  max: 60000    },
];

const SIZE_BUCKETS = [
  { label: 'All',                  min: 0,     max: Infinity },
  { label: 'Small (<3,000)',        min: 0,     max: 3000     },
  { label: 'Medium (3k–15k)',       min: 3000,  max: 15000    },
  { label: 'Large (15k–30k)',       min: 15000, max: 30000    },
  { label: 'Very Large (30k+)',     min: 30000, max: Infinity },
];

function fmt(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US').format(n);
}
function fmtTuition(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}
function fmtRate(n) {
  if (n == null) return '—';
  return (n * 100).toFixed(1) + '%';
}

function SelectivenessBadge({ rate }) {
  if (rate == null) return null;
  const pct = rate * 100;
  let label, cls;
  if (pct < 10)      { label = 'Highly Selective'; cls = 'badge-highly'; }
  else if (pct < 25) { label = 'Very Selective';   cls = 'badge-very'; }
  else if (pct < 50) { label = 'Selective';         cls = 'badge-selective'; }
  else if (pct < 75) { label = 'Accessible';        cls = 'badge-accessible'; }
  else               { label = 'Open';              cls = 'badge-open'; }
  return <span className={`sel-badge ${cls}`}>{label}</span>;
}

const CACHE_KEY = 'cf_colleges_v1';

function loadCachedColleges() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function saveCollegesCache(colleges) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(colleges)); } catch {}
}

async function fetchPage(page) {
  const fields = [
    'school.name',
    'school.city',
    'school.state',
    'school.ownership',
    'school.school_url',
    'latest.admissions.sat_scores.average.overall',
    'latest.admissions.admission_rate.overall',
    'latest.cost.tuition.out_of_state',
    'latest.student.size',
  ].join(',');

  const url =
    `https://api.data.gov/ed/collegescorecard/v1/schools` +
    `?api_key=${API_KEY}` +
    `&school.degrees_awarded.highest=3,4` +
    `&fields=${fields}` +
    `&per_page=100` +
    `&page=${page}`;

  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`API error ${resp.status}`);
  const data = await resp.json();
  return { results: data.results || [], total: data.metadata?.total ?? 0 };
}

function processSchool(s) {
  return {
    name:           s['school.name'],
    city:           s['school.city'],
    state:          s['school.state'],
    type:           s['school.ownership'],
    url:            s['school.school_url'],
    avgSAT:         s['latest.admissions.sat_scores.average.overall'],
    acceptanceRate: s['latest.admissions.admission_rate.overall'],
    tuition:        s['latest.cost.tuition.out_of_state'],
    size:           s['latest.student.size'],
  };
}

export default function Rankings() {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [error, setError]               = useState(null);
  const [currentPage, setCurrentPage]   = useState(1);
  const [filtersOpen, setFiltersOpen]   = useState(true);

  const [nameSearch,       setNameSearch]       = useState('');
  const [filterState,      setFilterState]      = useState('All');
  const [filterType,       setFilterType]       = useState('All');
  const [filterAcceptance, setFilterAcceptance] = useState('All');
  const [filterTuition,    setFilterTuition]    = useState('All');
  const [filterSize,       setFilterSize]       = useState('All');
  const [filterSATMin,     setFilterSATMin]     = useState('');
  const [filterSATMax,     setFilterSATMax]     = useState('');
  const [sortBy,           setSortBy]           = useState('satHighToLow');

  // Profile-based pre-fill
  const [profileBanner,    setProfileBanner]    = useState(null);
  const [studentProfile,   setStudentProfile]   = useState(null);

  // Shortlist
  const [shortlist,        setShortlist]        = useState(loadShortlist);
  const [showShortlist,    setShowShortlist]     = useState(false);

  // Gap analysis modal
  const [analysisTarget,   setAnalysisTarget]   = useState(null); // uni object or null

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Check sessionStorage cache first — avoids re-fetching on every visit
        const cached = loadCachedColleges();
        if (cached && cached.length > 0) {
          if (!cancelled) {
            setUniversities(cached);
            setLoadProgress(100);
            setLoading(false);
          }
          return;
        }

        const first = await fetchPage(0);
        if (cancelled) return;
        const totalPages = Math.ceil(first.total / 100);
        let allSchools = first.results.map(processSchool);
        setUniversities([...allSchools]);
        setLoadProgress(Math.round((1 / totalPages) * 100));

        for (let page = 1; page < totalPages; page++) {
          if (cancelled) return;
          const { results } = await fetchPage(page);
          allSchools = [...allSchools, ...results.map(processSchool)];
          setUniversities([...allSchools]);
          setLoadProgress(Math.round(((page + 1) / totalPages) * 100));
        }
        saveCollegesCache(allSchools);
        setLoading(false);
      } catch (err) {
        if (!cancelled) { setError(err.message); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pre-fill filters from student profile + store profile for gap analysis
  useEffect(() => {
    const profile = loadStudentProfile();
    if (!profile) return;
    setStudentProfile(profile);
    const sat = parseSATScore(profile.testScores);
    if (sat) {
      setFilterSATMin(String(Math.max(400, sat - 150)));
      setProfileBanner(`Filters pre-loaded from your Profile Builder results (SAT ~${sat})`);
    }
  }, []);

  const resetFilters = useCallback(() => {
    setNameSearch(''); setFilterState('All'); setFilterType('All');
    setFilterAcceptance('All'); setFilterTuition('All'); setFilterSize('All');
    setFilterSATMin(''); setFilterSATMax(''); setSortBy('satHighToLow');
    setCurrentPage(1);
    setProfileBanner(null);
  }, []);

  // Shortlist helpers
  const toggleShortlist = useCallback((uni) => {
    setShortlist(prev => {
      const exists = prev.some(s => s.name === uni.name);
      const next = exists ? prev.filter(s => s.name !== uni.name) : [...prev, uni];
      persistShortlist(next);
      return next;
    });
  }, []);

  const isShortlisted = useCallback((uni) => {
    return shortlist.some(s => s.name === uni.name);
  }, [shortlist]);

  const filtered = useMemo(() => {
    let arr = [...universities];

    if (nameSearch.trim()) {
      const q = nameSearch.toLowerCase();
      arr = arr.filter(u => u.name?.toLowerCase().includes(q));
    }
    if (filterState !== 'All') arr = arr.filter(u => u.state === filterState);
    if (filterType  !== 'All') arr = arr.filter(u => String(u.type) === filterType);

    if (filterAcceptance !== 'All') {
      const bucket = ACCEPTANCE_BUCKETS.find(b => b.label === filterAcceptance);
      if (bucket) arr = arr.filter(u => {
        if (u.acceptanceRate == null) return false;
        const pct = u.acceptanceRate * 100;
        return pct >= bucket.min && pct < bucket.max;
      });
    }
    if (filterTuition !== 'All') {
      const bucket = TUITION_BUCKETS.find(b => b.label === filterTuition);
      if (bucket) arr = arr.filter(u => u.tuition != null && u.tuition <= bucket.max);
    }
    if (filterSize !== 'All') {
      const bucket = SIZE_BUCKETS.find(b => b.label === filterSize);
      if (bucket) arr = arr.filter(u => u.size != null && u.size >= bucket.min && u.size < bucket.max);
    }
    if (filterSATMin) arr = arr.filter(u => u.avgSAT != null && u.avgSAT >= parseInt(filterSATMin));
    if (filterSATMax) arr = arr.filter(u => u.avgSAT != null && u.avgSAT <= parseInt(filterSATMax));

    switch (sortBy) {
      case 'satHighToLow':        arr.sort((a, b) => (b.avgSAT || 0) - (a.avgSAT || 0)); break;
      case 'satLowToHigh':        arr.sort((a, b) => (a.avgSAT || 0) - (b.avgSAT || 0)); break;
      case 'acceptanceLowToHigh': arr.sort((a, b) => (a.acceptanceRate ?? 1) - (b.acceptanceRate ?? 1)); break;
      case 'acceptanceHighToLow': arr.sort((a, b) => (b.acceptanceRate ?? 0) - (a.acceptanceRate ?? 0)); break;
      case 'tuitionLowToHigh':    arr.sort((a, b) => (a.tuition || 0) - (b.tuition || 0)); break;
      case 'tuitionHighToLow':    arr.sort((a, b) => (b.tuition || 0) - (a.tuition || 0)); break;
      case 'sizeLargeFirst':      arr.sort((a, b) => (b.size || 0) - (a.size || 0)); break;
      case 'nameAZ':              arr.sort((a, b) => (a.name || '').localeCompare(b.name || '')); break;
      default: break;
    }
    return arr;
  }, [universities, nameSearch, filterState, filterType, filterAcceptance, filterTuition, filterSize, filterSATMin, filterSATMax, sortBy]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated  = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const activeFilters = [
    nameSearch && `"${nameSearch}"`,
    filterState !== 'All' && filterState,
    filterType  !== 'All' && SCHOOL_TYPE_LABELS[Number(filterType)],
    filterAcceptance !== 'All' && filterAcceptance,
    filterTuition !== 'All' && filterTuition,
    filterSize    !== 'All' && filterSize,
    (filterSATMin || filterSATMax) && `SAT ${filterSATMin || '400'}–${filterSATMax || '1600'}`,
  ].filter(Boolean);

  return (
    <div className="cf-page">

      {/* ── Gap analysis modal ── */}
      {analysisTarget && (
        <GapAnalysisModal
          uni={analysisTarget}
          profile={studentProfile}
          onClose={() => setAnalysisTarget(null)}
        />
      )}

      {/* ── Top bar ── */}
      <div className="cf-topbar">
        <div className="cf-topbar-left">
          <Link to="/home" className="cf-back-btn">← Home</Link>
          <div>
            <h1 className="cf-title">College Finder</h1>
            <p className="cf-subtitle">
              {loading
                ? `Loading… ${universities.length.toLocaleString()} colleges so far`
                : `${filtered.length.toLocaleString()} of ${universities.length.toLocaleString()} colleges`}
            </p>
          </div>
        </div>
        <div className="cf-topbar-right">
          <input
            className="cf-search"
            type="text"
            placeholder="Search by name…"
            value={nameSearch}
            onChange={e => { setNameSearch(e.target.value); setCurrentPage(1); }}
          />
          <select
            className="cf-select"
            value={sortBy}
            onChange={e => { setSortBy(e.target.value); setCurrentPage(1); }}
          >
            <option value="satHighToLow">SAT: High → Low</option>
            <option value="satLowToHigh">SAT: Low → High</option>
            <option value="acceptanceLowToHigh">Most Selective First</option>
            <option value="acceptanceHighToLow">Least Selective First</option>
            <option value="tuitionLowToHigh">Tuition: Low → High</option>
            <option value="tuitionHighToLow">Tuition: High → Low</option>
            <option value="sizeLargeFirst">Enrollment: Largest First</option>
            <option value="nameAZ">Name A–Z</option>
          </select>
          <button
            className={`cf-filter-toggle cf-shortlist-btn${showShortlist ? ' active' : ''}`}
            onClick={() => setShowShortlist(o => !o)}
          >
            ♥ Saved {shortlist.length > 0 && <span className="cf-badge">{shortlist.length}</span>}
          </button>
          <button className="cf-filter-toggle" onClick={() => setFiltersOpen(o => !o)}>
            {filtersOpen ? 'Hide Filters' : '⚙ Filters'}
            {activeFilters.length > 0 && <span className="cf-badge">{activeFilters.length}</span>}
          </button>
        </div>
      </div>

      {/* ── Profile banner ── */}
      {profileBanner && (
        <div className="cf-profile-banner">
          <span>✨ {profileBanner}</span>
          <button className="cf-banner-reset" onClick={resetFilters}>Reset to default</button>
        </div>
      )}

      {/* ── Shortlist panel ── */}
      {showShortlist && (
        <div className="cf-shortlist-panel">
          <div className="cf-shortlist-header">
            <h3 className="cf-shortlist-title">♥ Saved Colleges ({shortlist.length})</h3>
            <button className="cf-filter-toggle" onClick={() => setShowShortlist(false)}>Close</button>
          </div>
          {shortlist.length === 0
            ? <p className="cf-shortlist-empty">No saved colleges yet — click ♡ on any card to save it.</p>
            : <div className="cf-grid">
                {shortlist.map((uni, i) => (
                  <CollegeCard
                    key={`sl-${uni.name}-${i}`}
                    uni={uni}
                    saved={true}
                    onToggleSave={toggleShortlist}
                    onAnalyze={setAnalysisTarget}
                    hasProfile={!!studentProfile}
                  />
                ))}
              </div>
          }
        </div>
      )}

      {/* ── Load progress ── */}
      {loading && (
        <div className="cf-progress-wrap">
          <div className="cf-progress-track">
            <div className="cf-progress-bar" style={{ width: `${loadProgress}%` }} />
          </div>
          <span className="cf-progress-label">{loadProgress}% · {universities.length.toLocaleString()} colleges loaded</span>
        </div>
      )}

      {/* ── Active filter chips ── */}
      {activeFilters.length > 0 && (
        <div className="cf-chips">
          {activeFilters.map(f => (
            <span key={f} className="cf-chip">{f}</span>
          ))}
          <button className="cf-chip cf-chip-clear" onClick={resetFilters}>✕ Clear all</button>
        </div>
      )}

      {/* ── Filter panel ── */}
      {filtersOpen && (
        <div className="cf-filters">
          <div className="cf-filter-row">
            <FilterGroup label="State">
              <select className="cf-select" value={filterState}
                onChange={e => { setFilterState(e.target.value); setCurrentPage(1); }}>
                {STATE_OPTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </FilterGroup>

            <FilterGroup label="School Type">
              <select className="cf-select" value={filterType}
                onChange={e => { setFilterType(e.target.value); setCurrentPage(1); }}>
                <option value="All">All Types</option>
                <option value="1">Public</option>
                <option value="2">Private Nonprofit</option>
                <option value="3">Private For-Profit</option>
              </select>
            </FilterGroup>

            <FilterGroup label="Selectivity">
              <select className="cf-select" value={filterAcceptance}
                onChange={e => { setFilterAcceptance(e.target.value); setCurrentPage(1); }}>
                {ACCEPTANCE_BUCKETS.map(b => <option key={b.label}>{b.label}</option>)}
              </select>
            </FilterGroup>

            <FilterGroup label="Intl. Tuition">
              <select className="cf-select" value={filterTuition}
                onChange={e => { setFilterTuition(e.target.value); setCurrentPage(1); }}>
                {TUITION_BUCKETS.map(b => <option key={b.label}>{b.label}</option>)}
              </select>
            </FilterGroup>

            <FilterGroup label="Enrollment Size">
              <select className="cf-select" value={filterSize}
                onChange={e => { setFilterSize(e.target.value); setCurrentPage(1); }}>
                {SIZE_BUCKETS.map(b => <option key={b.label}>{b.label}</option>)}
              </select>
            </FilterGroup>

            <FilterGroup label="SAT Range">
              <div className="cf-sat-range">
                <input className="cf-input" type="number" placeholder="Min" min="400" max="1600"
                  value={filterSATMin} onChange={e => { setFilterSATMin(e.target.value); setCurrentPage(1); }} />
                <span>–</span>
                <input className="cf-input" type="number" placeholder="Max" min="400" max="1600"
                  value={filterSATMax} onChange={e => { setFilterSATMax(e.target.value); setCurrentPage(1); }} />
              </div>
            </FilterGroup>
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {error && <p className="cf-error">⚠ {error}</p>}

      {/* ── Results ── */}
      {!error && (
        <>
          {paginated.length === 0 && !loading ? (
            <div className="cf-empty">
              <p>No colleges match your filters.</p>
              <button className="cf-btn-reset" onClick={resetFilters}>Reset Filters</button>
            </div>
          ) : (
            <div className="cf-grid">
              {paginated.map((uni, i) => (
                <CollegeCard
                  key={`${uni.name}-${i}`}
                  uni={uni}
                  saved={isShortlisted(uni)}
                  onToggleSave={toggleShortlist}
                  onAnalyze={setAnalysisTarget}
                  hasProfile={!!studentProfile}
                />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="cf-pagination">
              <button className="cf-page-btn" disabled={currentPage === 1}
                onClick={() => { setCurrentPage(p => p - 1); window.scrollTo(0, 0); }}>
                ← Prev
              </button>
              <span className="cf-page-info">
                Page {currentPage} of {totalPages} &nbsp;·&nbsp; {filtered.length.toLocaleString()} results
              </span>
              <button className="cf-page-btn" disabled={currentPage === totalPages}
                onClick={() => { setCurrentPage(p => p + 1); window.scrollTo(0, 0); }}>
                Next →
              </button>
            </div>
          )}

          <p className="cf-data-note">
            Data sourced from the US College Scorecard (data.gov) and may reflect a prior academic year. Tuition, acceptance rates, and SAT ranges can change — always verify on the official university website before making decisions.
          </p>
        </>
      )}
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="cf-filter-group">
      <label className="cf-filter-label">{label}</label>
      {children}
    </div>
  );
}

function CollegeCard({ uni, saved, onToggleSave, onAnalyze, hasProfile }) {
  return (
    <div className={`cf-card${saved ? ' cf-card--saved' : ''}`}>
      <div className="cf-card-top">
        <h3 className="cf-card-name">
          {uni.url
            ? <a href={`https://${uni.url}`} target="_blank" rel="noopener noreferrer">{uni.name}</a>
            : uni.name}
        </h3>
        <div className="cf-card-badges">
          <SelectivenessBadge rate={uni.acceptanceRate} />
          <button
            className={`cf-save-btn${saved ? ' cf-save-btn--saved' : ''}`}
            onClick={() => onToggleSave(uni)}
            title={saved ? 'Remove from saved' : 'Save college'}
          >{saved ? '♥' : '♡'}</button>
        </div>
      </div>

      <p className="cf-card-location">
        📍 {uni.city}, {uni.state}
        {uni.type != null && (
          <span className="cf-type-pill">{SCHOOL_TYPE_LABELS[uni.type]}</span>
        )}
      </p>

      <div className="cf-card-stats">
        <Stat label="Avg SAT"       value={fmt(uni.avgSAT)} />
        <Stat label="Acceptance"    value={fmtRate(uni.acceptanceRate)} />
        <Stat label="Intl. Tuition" value={fmtTuition(uni.tuition)} />
        <Stat label="Enrollment"    value={fmt(uni.size)} />
      </div>
      <button
        className={`cf-analyze-btn${!hasProfile ? ' cf-analyze-btn--dim' : ''}`}
        onClick={() => onAnalyze(uni)}
        title={hasProfile ? 'Analyze your fit for this school' : 'Complete Profile Builder to unlock fit analysis'}
      >
        🎯 Analyze Fit{!hasProfile && ' — complete Profile Builder first'}
      </button>
    </div>
  );
}

// ── Gap Analysis Modal ─────────────────────────────────────────────────────────
function GapAnalysisModal({ uni, profile, onClose }) {
  const [text,        setText]        = useState('');
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [modelChoice, setModelChoice] = useState(
    () => localStorage.getItem('gapModel') || 'gemini'
  );

  useEffect(() => {
    localStorage.setItem('gapModel', modelChoice);
  }, [modelChoice]);

  useEffect(() => {
    if (!profile) { setLoading(false); return; }
    setText(''); setError(null); setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const prompt = buildGapPrompt(uni, profile);
        let result;
        if (modelChoice === 'gpt') {
          try {
            result = await askOpenAI(prompt);
          } catch (e) {
            // fallback to Gemini if GPT fails
            const res = await gapModel.generateContent(prompt);
            result = await res.response.text();
          }
        } else {
          const res = await gapModel.generateContent(prompt);
          result = await res.response.text();
        }
        if (!cancelled) { setText(result); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setError(e.message); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [uni, profile, modelChoice]);

  return (
    <div className="gap-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="gap-modal">
        <div className="gap-modal-header">
          <div>
            <h2 className="gap-modal-title">🎯 Fit Analysis</h2>
            <p className="gap-modal-school">
              {uni.name} &nbsp;·&nbsp; {uni.city}, {uni.state}
              {uni.type != null && <span className="gap-type-pill">{SCHOOL_TYPE_LABELS[uni.type]}</span>}
            </p>
          </div>
          <div className="gap-modal-controls">
            <select
              className="gap-model-select"
              value={modelChoice}
              onChange={e => setModelChoice(e.target.value)}
              disabled={loading}
              title="Switch AI model — re-runs analysis automatically"
            >
              <option value="gemini">Gemini 2.5 Flash</option>
              <option value="gpt">GPT-3.5 Turbo</option>
            </select>
            <button className="gap-close" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>

        <div className="gap-modal-stats">
          <span>SAT avg: <strong>{uni.avgSAT ?? '—'}</strong></span>
          <span>Acceptance: <strong>{uni.acceptanceRate != null ? (uni.acceptanceRate*100).toFixed(1)+'%' : '—'}</strong></span>
          <span>Intl. Tuition: <strong>{uni.tuition ? '$'+uni.tuition.toLocaleString() : '—'}</strong></span>
        </div>

        <div className="gap-modal-body">
          {!profile && (
            <div className="gap-no-profile">
              <p>Complete your <strong>Profile Builder</strong> first to unlock personalised fit analysis.</p>
              <Link to="/profile-builder"><button className="gap-cta-btn">Go to Profile Builder →</button></Link>
            </div>
          )}
          {profile && loading && (
            <div className="gap-loading">
              <div className="gap-spinner" />
              <span>Analysing your fit for {uni.name}…</span>
            </div>
          )}
          {error && <p className="gap-error">⚠ {error}</p>}
          {text  && <GapAnalysisText text={text} />}
        </div>
      </div>
    </div>
  );
}

// ── Gap Analysis Text renderer ─────────────────────────────────────────────────
function GapAnalysisText({ text }) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

  return (
    <div className="gap-content">
      {lines.map((line, i) => {
        if (line.startsWith('OVERALL FIT:')) {
          const val = line.replace('OVERALL FIT:', '').trim();
          const cls = /likely/i.test(val)            ? 'fit-likely'
                    : /significant reach/i.test(val) ? 'fit-significant'
                    : /reach/i.test(val)              ? 'fit-reach'
                    :                                   'fit-match';
          return (
            <div key={i} className="gap-fit-row">
              <span className={`gap-fit-badge ${cls}`}>{val.split('—')[0].trim()}</span>
              {val.includes('—') && <span className="gap-fit-reason">{val.split('—').slice(1).join('—').trim()}</span>}
            </div>
          );
        }
        if (/^(QUANTITATIVE|STRENGTHS|GAPS|BOTTOM LINE):/.test(line)) {
          return <h3 key={i} className="gap-section-head">{line.replace(/:$/, '')}</h3>;
        }
        if (line.startsWith('•')) {
          return <p key={i} className="gap-bullet">{line}</p>;
        }
        return <p key={i} className="gap-para">{line}</p>;
      })}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="cf-stat">
      <span className="cf-stat-label">{label}</span>
      <span className="cf-stat-value">{value}</span>
    </div>
  );
}
